import { useState } from 'react';
import { ButtonBase, Chip, Collapse, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Clear as ClearIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import { useI18n } from '../../i18n/useI18n';

type Props = {
  startDate: string;
  endDate: string;
  onStartDate: (value: string) => void;
  onEndDate: (value: string) => void;
  onClear: () => void;
  accentColor?: string;
};

const GalleryDateFilter: React.FC<Props> = ({
  startDate,
  endDate,
  onStartDate,
  onEndDate,
  onClear,
  accentColor = '#e86a8a',
}) => {
  const { pages: t } = useI18n();
  const [open, setOpen] = useState(false);
  const active = Boolean(startDate || endDate);

  return (
    <>
      <ButtonBase
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.7,
          borderRadius: 2,
          border: '1px solid',
          borderColor: open || active ? alpha(accentColor, 0.35) : alpha(accentColor, 0.12),
          bgcolor: open || active ? alpha(accentColor, 0.08) : alpha(accentColor, 0.03),
          color: accentColor,
        }}
      >
        <FilterListIcon sx={{ fontSize: 16 }} />
        <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
          {t.photos.filterByDate}
        </Typography>
        {active && (
          <Chip
            size="small"
            label="ON"
            sx={{
              height: 16,
              fontSize: '0.6rem',
              fontWeight: 800,
              bgcolor: accentColor,
              color: '#fff',
              '& .MuiChip-label': { px: 0.6 },
            }}
          />
        )}
      </ButtonBase>
      {active && (
        <Tooltip title={t.photos.clearFilter}>
          <IconButton
            size="small"
            onClick={onClear}
            sx={{ color: accentColor, bgcolor: alpha(accentColor, 0.08) }}
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      <Collapse in={open} timeout={220} sx={{ flexBasis: '100%' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            type="datetime-local"
            label={t.common.fromDate}
            value={startDate}
            onChange={(e) => onStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            type="datetime-local"
            label={t.common.toDate}
            value={endDate}
            onChange={(e) => onEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Collapse>
    </>
  );
};

export default GalleryDateFilter;
